import React, { useEffect } from "react";

import _ from "lodash";
import cx from "classnames";

import styles from "./chat.module.scss";
import { Simulation, SimulationEditorChatState } from "src/types/coaching/simulation";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { createSimulationEditorChat } from "src/redux/actions/coaching/simulation";

import { useScrollToBottom, useSticky } from 'react-scroll-to-bottom';


interface ChatContainerProps {
  simulation: Simulation;
  configurationType: "persona" | "organization" | "objectives" | "assessment";
  getParameters: () => any;
  onChange: (value: any) => void;
}

export const ChatContainer: React.FC<ChatContainerProps> = ({ simulation, configurationType, getParameters, onChange }) => {
  const dispatch = useDispatch();

  const scrollToBottom = useScrollToBottom();
  const [sticky] = useSticky();

  const { session, chats, creating, created, parameters } = useSelector((state: any) => {
    const editState = state.coaching.simulations.edit;
    if (editState === null || editState.item?.id != simulation.id) {
      return {
        session: null,
        chats: []
      }
    }

    const sessionState = editState.editorSessions.find((session: any) => session.configurationType === configurationType);
    const session = sessionState?.session;
    const chats = sessionState?.chats || [];

    const lastChat = _.last(chats) as SimulationEditorChatState;
    const creating = lastChat?.creating || false;
    const created = lastChat?.created || false;
    const parameters = lastChat?.parameters || null;

    return {
      session: session,
      chats: chats,
      creating: creating,
      created: created,
      parameters: parameters,
    }
  });

  const handleSubmit = (input: string) => {
    console.log("User input submitted:", input);
    dispatch(createSimulationEditorChat({
      simulation: simulation.id!,
      session: session,
      configurationType: configurationType,
      input: input,
      parameters: getParameters(),
    }));
  }

  useEffect(() => {
    if (created && parameters) {
      onChange(parameters);
    }
  }, [created]);

  return (
    <>
      <div className={styles.container}>
        <div className={styles.chats}>
          {chats.map((chat: SimulationEditorChatState, index: number) => (
            <ChatView key={index} chat={chat} />
          ))}
        </div>

        <UserInput onSubmit={handleSubmit} />
      </div>
      {!sticky && (
        <div className={styles.downArrow} onClick={scrollToBottom}>
          <i className={cx(styles.icon, 'paperflite-down-arrow')} />
        </div>
      )}
    </>
  );
}

interface ChatViewProps {
  chat: SimulationEditorChatState;
}

const ChatView: React.FC<ChatViewProps> = ({ chat }) => {
  return (
    <div className={styles.chatView}>
      <div className={styles.userRequest}>
        {chat.input}
      </div>
      <div className={styles.aiResponse}>
        {chat.creating && 'processing your request...'}
        {chat.created && chat.output}
      </div>
    </div>
  );
}

interface UserInputProps {
  disabled?: boolean;
  // eslint-disable-next-line no-unused-vars
  onSubmit: (input: string) => void;
}

const UserInput: React.FC<UserInputProps> = ({ disabled, onSubmit }) => {
  const [input, setInput] = React.useState("");
  const formRef = React.useRef<HTMLFormElement>(null);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInput(event.target.value);
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!disabled && input.trim() !== "") {
      onSubmit(input);
      setInput("");
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      formRef.current?.requestSubmit();
    }
  }

  return (
    <div className={styles.userInput}>
      <form ref={formRef} className={styles.form} onSubmit={handleSubmit}>
        <i className={cx(styles.icon, 'paperflite-seek')} />
        <input className={styles.input}
          placeholder="Type your persona..."
          disabled={disabled}
          value={input}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
        />
      </form>
    </div>
  );
}